/**
 * AZ „EREDETI" — a papír maga, a képernyőn.
 *
 * MIÉRT PANEL ÉS NEM LINK. A `D9` a TUI elvetését ezzel indokolta ELSŐKÉNT:
 * „a munkalista közepe egy nagyban megjelenített szkennelt számla — ez a
 * bizonyíték". A `§8c`/8 ugyanezt mondja ki a szeletre: a dokumentum
 * „tárolódik ÉS LÁTSZIK". Egy új fülre mutató link ezt nem teljesíti: a
 * könyvelő a döntést a bizonyíték nélküli képernyőn hozná meg, és
 * ablakot váltogatna minden bizonylatnál — sor-módban másodpercenként.
 *
 * HÁROM MÉRET, EGY PAPÍR. A panel a MUNKA közben mutat (a hasáb szélességén);
 * a „Teljes képernyő" ugyanazt a keretet nagyítja fel, ELHAGYÁS NÉLKÜL — a
 * munkalista a háttérben marad, az `Esc` egy leütéssel visszahoz; a
 * „Megnyitom" pedig új fülre viszi a fájlt.
 *
 * A KÖZÉPSŐ A LÉNYEG: a soktételes számlát a könyvelőnek EL KELL OLVASNIA,
 * de nem akar közben elveszíteni egy félig kitöltött kontírt. A teljes
 * képernyő ezért nem navigáció — ugyanaz a DOM, csak nagyban.
 *
 * A PAPÍRT A SAJÁT NÉZŐ RAJZOLJA (`viewer/`, #64), nem `<img>` és nem a
 * böngésző PDF-nézője egy `<iframe>`-ben: a jelölőket a papírra kell tűzni,
 * és ahhoz tudni kell, melyik oldal, mekkora nagyítás, merre forgatva. A
 * nagyítás, a forgatás, a lapozás, a letöltés és a nyomtatás a néző
 * eszköztárán van — képre és PDF-re ugyanúgy.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { embedKind } from './original';
import type { DocumentFileState } from './useDocumentFile';
import { DocumentViewer } from './viewer/DocumentViewer';
import type { PaperNotes } from './viewer/notes';

export function DocumentOriginal({
  state,
  notes,
  onExit,
  onNext,
}: {
  state: DocumentFileState;
  /** A papírra tűzött jegyzetek (#64) — a puska sorai. */
  notes?: PaperNotes;
  /** `←` a papír bal szélén, vagy `Esc`: vissza a kupacra (#68). */
  onExit?: () => void;
  /** `→` a papír jobb szélén: tovább a döntés hasábjára (#68). */
  onNext?: () => void;
}) {
  const { t } = useTranslation();
  const frame = useRef<HTMLDivElement>(null);
  const file = state.status === 'ready' ? state.file : null;

  /**
   * A GOMB CSAK AKKOR VAN OTT, HA VAN MIT NAGYÍTANI ÉS LEHET IS.
   *
   * Üres panelre vagy a „nem jeleníthető meg" mondatra teljes képernyőt kérni
   * értelmetlen: a gomb egy fekete téglalapot nyitna, és a könyvelő azt hinné,
   * elromlott.
   *
   * A `fullscreenEnabled` a MÁSIK fele, és nem elméleti: ahol az elem-szintű
   * teljes képernyő nincs meg (régebbi Safari, iPadOS, beágyazott webnézet),
   * ott a `requestFullscreen` sincs — az opcionális hívás némán elszáll, és
   * marad egy gomb, ami KATTINTHATÓ, DE NEM CSINÁL SEMMIT. Az ennél a panelnél
   * külön rossz: minden más ága kimondja, mi történik.
   * *(Code review fogta meg, 09-04.)*
   */
  const enlargeable =
    file !== null && embedKind(file.mimeType) !== 'none' && document.fullscreenEnabled;

  // `<section>`, NEM `<aside>`: az `aside` szerepe „kiegészítő tartalom", ez
  // viszont a BIZONYÍTÉK — a `D9` szerint épp ez a képernyő létjogosultsága.
  // A kupac és a részletpanel is `<section>`, tehát a munkalista három hasábja
  // három egyenrangú, megnevezett terület a képernyőolvasóban is.
  return (
    <section className="worklist__original" aria-label={t('worklist.original.heading')}>
      <header className="original__head">
        <span className="original__label">{t('worklist.original.heading')}</span>
        {/* A FÁJLNÉV AZONOSÍT, nem díszít: a `2.6/10` szerint az eredetit
            változatlanul őrizzük, és a könyvelő ezen a néven találja meg a
            saját gépén is, ha letölti. */}
        {file === null ? null : (
          <span className="original__filename" title={file.originalFilename}>
            {file.originalFilename}
          </span>
        )}
        {enlargeable ? (
          <button
            type="button"
            className="original__enlarge"
            // A `title` a KILÉPÉST mondja el, mert azt nem lehet kitalálni:
            // teljes képernyőn a panel fejléce — és vele ez a gomb — eltűnik,
            // tehát a visszaút csak az `Esc`. A böngésző maga is kiírja
            // egyszer, de az elszáll pár másodperc alatt.
            // A SÚGÓ SZÖVEGKÉNT IS OTT VAN (`aria-describedby`), nem csak
            // `title`-ben: a `title` egérre való — billentyűzettel nem
            // elérhető, és a képernyőolvasók sem egyformán mondják ki. Teljes
            // képernyőn viszont a fejléc ELTŰNIK, tehát a kilépés módja épp az
            // az egy dolog, amit a billentyűzetes könyvelő nem tudhat meg
            // máshonnan (`2.6/16`). *(Code review fogta meg, 09-04.)*
            aria-describedby="original-fullscreen-hint"
            onClick={() => {
              // A TELJES KÉPERNYŐ A KERETRE MEGY, nem az egész panelre: a
              // fejléc nagyítva csak a helyet venné el a papírtól.
              //
              // FELTÉTELES HÍVÁS. A `requestFullscreen` nem mindenhol
              // létezik (régi böngésző, beágyazott nézet, jsdom), és a
              // visszaadott ígéret ELUTASÍTHAT, ha a böngésző megtagadja —
              // egy kezeletlen elutasítás a konzolban landolna, miközben a
              // könyvelőnek nincs mit tennie vele. A papír a helyén marad.
              void frame.current?.requestFullscreen?.().catch(() => undefined);
            }}
          >
            {t('worklist.original.fullscreen')}
          </button>
        ) : null}
        {enlargeable ? (
          <span id="original-fullscreen-hint" className="visually-hidden">
            {t('worklist.original.fullscreenHint')}
          </span>
        ) : null}
        {file === null ? null : (
          <a
            className="original__open"
            href={file.url}
            target="_blank"
            rel="noreferrer"
            // A CÍM ALÁÍRT, és amíg meg nem jött, NINCS LINK — egy jelen lévő,
            // de 401-be futó link rosszabb, mint a hiánya.
          >
            {t('worklist.openFile')}
          </a>
        )}
      </header>

      <div className="original__frame" ref={frame}>
        <Embedded state={state} notes={notes} onExit={onExit} onNext={onNext} />
      </div>
    </section>
  );
}

/**
 * A BEÁGYAZOTT TARTALOM — négy állapot, mind KIMONDVA.
 *
 * A néma üres keret a legrosszabb kimenet: a könyvelő nem tudja eldönteni,
 * hogy tölt, hogy elveszett, vagy hogy csak ő nem látja. Ezért nincs olyan
 * ág, ami semmit nem rajzol.
 *
 * A TÖLTÉS KÜLÖN ÁG, és ez nem finomkodás. Amíg egyben volt a bukással, a
 * panel MINDEN bizonylatváltásnál kiírta, hogy „nem tölthető be" — sor-módban
 * tehát minden `Enter` után, egy olyan betöltésről, ami éppen rendben zajlott.
 * *(Code review fogta meg, 09-04.)*
 */
function Embedded({
  state,
  notes,
  onExit,
  onNext,
}: {
  state: DocumentFileState;
  notes?: PaperNotes;
  onExit?: () => void;
  onNext?: () => void;
}) {
  const { t } = useTranslation();

  // MÉG TÖLT. Egy mondat áll itt, nem pörgő ikon: a papír nem a munka KAPUJA,
  // lefűzni enélkül is lehet, tehát a várakozás nem érdemel figyelemfelkeltést.
  if (state.status === 'loading') {
    return <p className="original__empty">{t('worklist.original.loading')}</p>;
  }

  // NEM JÖTT MEG. Ez nem a könyvelő döntése, és nincs mit tennie vele — a
  // mondat ezért kimondja azt is, hogy a lefűzést nem akadályozza.
  if (state.status === 'failed') {
    return <p className="original__empty">{t('worklist.original.unavailable')}</p>;
  }

  const { file } = state;
  const kind = embedKind(file.mimeType);

  if (kind !== 'none') {
    return <DocumentViewer file={file} kind={kind} notes={notes} onExit={onExit} onNext={onNext} />;
  }

  // AMIT A BÖNGÉSZŐ NEM RAJZOL KI (ma: HEIC/HEIF). A fájl MEGVAN — ezt ki
  // kell mondani, különben a könyvelő elveszettnek hiszi, és újra bekéri egy
  // olyan papírt, ami rég megérkezett.
  return <p className="original__empty">{t('worklist.original.notEmbeddable')}</p>;
}
